import { useEffect, useState, useCallback } from "react";
import api, { apiError, pollJob } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, FileText, Copy, ChevronLeft, Check, Loader2, Trash2, Landmark, Hash, Banknote, ExternalLink, Save } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const DOC_TYPES = [
  { key: "articles", label: "Articles of Incorporation" },
  { key: "bylaws", label: "Corporate Bylaws" },
  { key: "ein", label: "EIN (SS-4) Guide" },
  { key: "s_election", label: "Form 2553 (S-Corp) Guide" },
];

export default function BusinessSetup() {
  const [guides, setGuides] = useState({});
  const [entities, setEntities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ entity_name: "", entity_type: "LLC", state: "", industry: "" });

  const load = useCallback(() => api.get("/business-entities").then((r) => setEntities(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/entity-guides").then((r) => setGuides(r.data)).catch(() => {});
    load();
  }, [load]);

  const create = async () => {
    if (!form.entity_name.trim()) return toast.error("Entity name required");
    setSaving(true);
    try {
      const { data } = await api.post("/business-entities", form);
      toast.success("Entity created");
      setOpen(false);
      setForm({ entity_name: "", entity_type: "C-Corp", state: "", industry: "" });
      await load();
      setSelected(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (selected) return <EntityDetail entity={selected} guides={guides} onBack={() => { setSelected(null); load(); }} refresh={async () => { const r = await api.get(`/business-entities/${selected.id}`); setSelected(r.data); load(); }} />;

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-bold mb-2">Corporate Formation</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">Business Setup</h1>
          <p className="text-muted-foreground mt-3">Form an LLC, C-Corp or S-Corp, follow guided steps, and generate documents.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-lg" data-testid="add-entity-btn"><Plus className="h-4 w-4 mr-1" /> New Entity</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {["LLC", "C-Corp", "S-Corp"].map((t) => (
          <Card key={t} className="p-6 bg-card/60 border-border" data-testid={`guide-${t}`}>
            <div className="flex items-center gap-2 mb-2"><Building2 className="h-5 w-5 text-primary" /><h3 className="font-display text-xl font-semibold">{t}</h3></div>
            <p className="text-sm text-muted-foreground leading-relaxed">{guides[t]?.summary}</p>
            <div className="mt-3 text-xs"><span className="text-primary font-semibold uppercase tracking-widest">Best for: </span><span className="text-muted-foreground">{guides[t]?.best_for}</span></div>
            <div className="mt-3 text-xs text-muted-foreground">{guides[t]?.steps?.length} guided steps</div>
          </Card>
        ))}
      </div>

      <h3 className="font-display text-xl font-semibold mb-3">Your Entities</h3>
      <div className="grid gap-3">
        {entities.map((e) => {
          const total = guides[e.entity_type]?.steps?.length || 1;
          const pct = Math.round(((e.completed_steps?.length || 0) / total) * 100);
          return (
            <Card key={e.id} onClick={() => setSelected(e)} className="p-4 bg-card/60 border-border cursor-pointer hover:border-primary/40 transition-colors" data-testid={`entity-card-${e.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><span className="font-medium">{e.entity_name}</span><Badge variant="secondary">{e.entity_type}</Badge>{e.state && <span className="text-xs text-muted-foreground">{e.state}</span>}</div>
                <span className="text-sm font-mono">{pct}%</span>
              </div>
              <Progress value={pct} className="h-1.5" />
            </Card>
          );
        })}
        {!entities.length && <p className="text-sm text-muted-foreground py-8 text-center">No entities yet. Create your first corporation.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/90 backdrop-blur-xl border-border">
          <DialogHeader><DialogTitle className="font-display">New Business Entity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Entity Name *</Label><Input value={form.entity_name} onChange={(e) => setForm({ ...form, entity_name: e.target.value })} placeholder="Acme Holdings Inc." data-testid="entity-name" /></div>
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <div className="flex gap-2">
                {["LLC", "C-Corp", "S-Corp"].map((t) => (
                  <button key={t} type="button" onClick={() => setForm({ ...form, entity_type: t })} className={`flex-1 px-3 py-2 rounded-md text-sm border transition-colors ${form.entity_type === t ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`} data-testid={`entity-type-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Delaware" data-testid="entity-state" /></div>
              <div className="space-y-2"><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="entity-industry" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={create} disabled={saving} className="rounded-lg" data-testid="save-entity-btn">{saving ? "Creating…" : "Create Entity"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EntityDetail({ entity, guides, onBack, refresh }) {
  const guide = guides[entity.entity_type] || { steps: [] };
  const completed = new Set(entity.completed_steps || []);
  const [genDoc, setGenDoc] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);

  const toggle = async (key) => {
    await api.post(`/business-entities/${entity.id}/steps`, { step_key: key, completed: !completed.has(key) });
    refresh();
  };
  const genDocument = async (docType) => {
    setGenDoc(docType);
    try {
      const { data } = await api.post(`/business-entities/${entity.id}/documents`, { doc_type: docType });
      const doc = await pollJob(data.job_id);
      toast.success("Document generated");
      await refresh();
      setViewDoc(doc);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setGenDoc(null); }
  };
  const remove = async () => { await api.delete(`/business-entities/${entity.id}`); toast.success("Entity deleted"); onBack(); };
  const copy = (t) => copyText(t, "Copied");
  const pct = Math.round((completed.size / (guide.steps.length || 1)) * 100);

  return (
    <div className="animate-fade-up">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-white flex items-center gap-1 mb-4" data-testid="entity-back-btn"><ChevronLeft className="h-4 w-4" /> All entities</button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2"><h1 className="font-display text-3xl font-bold tracking-tight">{entity.entity_name}</h1><Badge variant="secondary">{entity.entity_type}</Badge></div>
          <p className="text-muted-foreground text-sm mt-1">{entity.state || "State TBD"} · {entity.industry || "Industry TBD"}</p>
        </div>
        <Button variant="ghost" className="text-destructive" onClick={remove} data-testid="delete-entity-btn"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
      </div>

      <Card className="p-5 bg-card/60 border-border mb-6">
        <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium">Formation Progress</span><span className="font-mono text-sm">{pct}%</span></div>
        <Progress value={pct} className="h-2" />
      </Card>

      <EntityPortal key={entity.id} entity={entity} refresh={refresh} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h3 className="font-display text-xl font-semibold mb-3">Guided Steps</h3>
          <div className="space-y-2">
            {guide.steps.map((s, i) => {
              const done = completed.has(s.key);
              return (
                <Card key={s.key} className={`p-4 border-border transition-colors ${done ? "bg-emerald-500/5 border-emerald-500/30" : "bg-card/60"}`} data-testid={`step-${s.key}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => toggle(s.key)} className={`mt-0.5 h-5 w-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${done ? "bg-emerald-500 border-emerald-500" : "border-muted-foreground/50"}`} data-testid={`step-toggle-${s.key}`}>
                      {done && <Check className="h-3 w-3 text-white" />}
                    </button>
                    <div>
                      <div className={`font-medium text-sm ${done ? "line-through text-muted-foreground" : ""}`}>{i + 1}. {s.title}</div>
                      <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.detail}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="font-display text-xl font-semibold mb-3">Documents</h3>
          <div className="space-y-2 mb-4">
            {DOC_TYPES.map((d) => (
              <Button key={d.key} variant="secondary" className="w-full justify-start rounded-lg" onClick={() => genDocument(d.key)} disabled={genDoc === d.key} data-testid={`gen-doc-${d.key}`}>
                {genDoc === d.key ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} {d.label}
              </Button>
            ))}
          </div>
          {(entity.documents || []).length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Generated</p>
              {entity.documents.map((doc) => (
                <button key={doc.id} onClick={() => setViewDoc(doc)} className="w-full text-left text-sm px-3 py-2 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center gap-2" data-testid={`view-doc-${doc.id}`}>
                  <FileText className="h-3.5 w-3.5" /> {doc.title}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center justify-between pr-6">
            <span>{viewDoc?.title}</span>
            <Button size="sm" variant="secondary" onClick={() => copy(viewDoc.content)} data-testid="copy-doc-btn"><Copy className="h-4 w-4 mr-1" /> Copy</Button>
          </DialogTitle></DialogHeader>
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed bg-background/50 p-5 rounded-lg border border-border" data-testid="doc-content">{viewDoc?.content}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EntityPortal({ entity, refresh }) {
  const [f, setF] = useState({
    ein_number: entity.ein_number || "",
    duns_number: entity.duns_number || "",
    funding_status: entity.funding_status || "",
    funding_notes: entity.funding_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/business-entities/${entity.id}`, f); toast.success("Portal saved"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const items = [
    { key: "ein_number", label: "EIN (Tax ID)", icon: Hash, placeholder: "XX-XXXXXXX", link: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online", linkLabel: "Apply for EIN (IRS)" },
    { key: "duns_number", label: "D-U-N-S Number (Dun & Bradstreet)", icon: Landmark, placeholder: "XX-XXX-XXXX", link: "https://www.dnb.com/duns-number/get-a-duns.html", linkLabel: "Get D-U-N-S Number" },
    { key: "funding_status", label: "Business Funding", icon: Banknote, placeholder: "e.g. Applied / Approved $25k", link: "https://www.nav.com/business-financing-options/", linkLabel: "Explore Funding" },
  ];
  return (
    <Card className="p-6 bg-gradient-to-br from-primary/10 to-transparent border-border mb-6" data-testid="entity-portal">
      <h3 className="font-display text-xl font-semibold mb-1 flex items-center gap-2"><Landmark className="h-5 w-5 text-primary" /> EIN · D-U-N-S · Funding Portal</h3>
      <p className="text-sm text-muted-foreground mb-4">Get your Tax ID, business credit file, and funding for this entity — then record the numbers here.</p>
      <div className="grid md:grid-cols-3 gap-4">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.key} className="rounded-lg border border-border bg-card/60 p-4">
              <div className="flex items-center gap-2 text-sm font-medium mb-2"><Icon className="h-4 w-4 text-primary" /> {it.label}</div>
              <Input value={f[it.key]} onChange={(e) => setF({ ...f, [it.key]: e.target.value })} placeholder={it.placeholder} data-testid={`portal-${it.key}`} />
              <a href={it.link} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`portal-link-${it.key}`}>{it.linkLabel} <ExternalLink className="h-3 w-3" /></a>
            </div>
          );
        })}
      </div>
      <div className="mt-3">
        <Input value={f.funding_notes} onChange={(e) => setF({ ...f, funding_notes: e.target.value })} placeholder="Funding notes (lender, amount, dates)…" data-testid="portal-funding_notes" />
      </div>
      <Button onClick={save} disabled={saving} className="rounded-lg mt-4" data-testid="save-portal-btn">{saving ? "Saving…" : <><Save className="h-4 w-4 mr-1" /> Save Portal</>}</Button>
    </Card>
  );
}
